/**
 * The alert kind table and its helpers — everything the overlay stack needs to
 * render, chime for, and route an alert card, with no React in the way.
 *
 * This was the top half of `CrmAlertHost.tsx` back when that host owned both the
 * data and the presentation. The host is gone (the D-2 overlay manager replaced
 * it); the kind table outlived it because it is the single source of truth for
 * what each alert LOOKS like and DOES, shared by OverlayHost, OverlayCards and
 * useAlertsFeed.
 *
 * Two things are genuinely different between kinds, not just styled differently:
 *   - Some DO NOT EXPIRE. The hot-lead card is a 15-minute "call them now"
 *     window and self-expires; a consented patient must not vanish off the
 *     screen unseen, so `referral_consented` has no TTL server-side and shows
 *     "waiting since …" instead of a countdown.
 *   - The 3-minute escalation is still real for the classic kinds, so the
 *     owner-side escalate-at-T-0 POST applies to both of them.
 */
import { Phone, FileCheck2, ClipboardCheck, AlertTriangle, UserRound, HelpCircle, Bot } from 'lucide-react';

export type AlertKind =
  | 'lead_call'
  | 'referral_consented'
  // Texting-agent escalations (texting-agent plan §6.7) — the popup-worthy
  // kinds ride this same stack so away-from-console moments are covered.
  | 'agent_call_request'
  | 'agent_handoff_review'
  | 'agent_hostile_or_distress'
  | 'agent_human_requested'
  // §6.7b human-first pair: these two DO have a client deadline — the agent
  // answers the lead itself when the window closes — so they show a countdown.
  | 'agent_unknown_answer'
  | 'agent_bot_question';

export interface CrmAlert {
  id: string;
  kind: AlertKind;
  contact_id: string;
  first_name: string | null;
  study_name: string | null;
  status: 'assigned' | 'escalated';
  assigned_user_id: string | null;
  assigned_user_name: string | null;
  created_at: string;
  escalate_at: string;
  /** lead_call only */
  eligibility?: 'eligible' | 'needs_review';
  /** referral_consented only */
  org_name?: string | null;
  provider_name?: string | null;
}

export const pad = (n: number) => String(n).padStart(2, '0');

/** A short two-note WebAudio chime — no asset file, no external request. The
 *  note pair is per-kind so a consent and a hot lead are distinguishable
 *  without looking at the screen. */
export function playChime(notes: [number, number]) {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const start = ctx.currentTime;
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t0 = start + i * 0.18;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.25, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.16);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t0 + 0.18);
    });
    setTimeout(() => { ctx.close().catch(() => {}); }, 800);
  } catch {
    /* autoplay blocked / unsupported — the OS notification + in-app card still fire */
  }
}

export interface KindDef {
  /** Header band. A state banner, not chrome — the CONTROLS below stay brand
   *  primary in every card, per the design system's single-accent rule. */
  band: string;
  ring: string;
  icon: typeof Phone;
  title: string;
  /** Primary action label; the button itself is always primary-600. */
  action: string;
  notes: [number, number];
  /** Small pill under the name, or null when the kind has none. */
  badge: (a: CrmAlert) => { label: string; cls: string } | null;
  /** Secondary line: study for a lead, referring practice for a referral. */
  detail: (a: CrmAlert) => string | null;
  /** Whether the card shows a countdown to escalation. */
  showsCountdown: boolean;
  /** Overrides the status line on an unassigned (broadcast) card. The §6.7b
   *  pair counts down to something the AGENT does, not to a re-route, so the
   *  generic "Escalated — call it if you can" would be a lie. */
  escalatedLine?: (ctx: { mmss: string; expired: boolean }) => string;
  href: (a: CrmAlert) => string;
  osTitle: string;
}

// Record<string, …> so the agent kinds can join via Object.assign below
// without forcing every kind into this literal; defFor falls back to
// lead_call for anything unknown.
export const KINDS: Record<string, KindDef> = {
  lead_call: {
    band: 'bg-primary-600',
    ring: 'border-primary-300 ring-primary-500/30 dark:border-primary-700',
    icon: Phone,
    title: 'Call this lead now',
    action: 'Call now',
    notes: [880, 1174.7], // A5 -> D6
    badge: (a) => (a.eligibility === 'eligible'
      ? { label: 'Eligible', cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' }
      : { label: 'Needs review', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' }),
    detail: (a) => a.study_name,
    showsCountdown: true,
    href: (a) => `/crm/contacts/${a.contact_id}`,
    osTitle: '📞 Call this lead now',
  },
  referral_consented: {
    // Green is SEMANTIC here (a consent landed), which the design system
    // sanctions for state; it is not a second brand accent.
    band: 'bg-green-600',
    ring: 'border-green-300 ring-green-500/30 dark:border-green-800',
    icon: FileCheck2,
    title: 'Referred patient just consented',
    action: 'Open patient',
    notes: [659.3, 987.8], // E5 -> B5, a rising pair distinct from the hot-lead chime
    badge: () => ({
      label: 'Referral',
      cls: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
    }),
    detail: (a) => a.provider_name || a.org_name || a.study_name,
    // No TTL server-side (P1-D18), so a countdown would imply a deadline that
    // does not exist.
    showsCountdown: false,
    href: (a) => `/crm/contacts/${a.contact_id}`,
    osTitle: '✅ Referred patient just consented',
  },
};

// Agent escalation cards (texting-agent plan §6.7). They share the stack's
// motion but not the hot-lead deadline: escalation re-routing is the step-7
// worker tick, so no countdown. Claiming navigates to the contact with the
// agent panel in view (it is the first tab of the lead page's rail);
// handoff_review deep-links the rail's Screening tab (C-20, ?tab=screening —
// LeadRail's tab keys are agent|next|screening|profile).
const agentKind = (over: Partial<KindDef> & Pick<KindDef, 'icon' | 'title' | 'action' | 'osTitle' | 'notes'>): KindDef => ({
  band: 'bg-primary-600',
  ring: 'border-primary-300 ring-primary-500/30 dark:border-primary-700',
  badge: () => ({
    label: 'Agent',
    cls: 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300',
  }),
  detail: (a) => a.study_name,
  showsCountdown: false,
  href: (a) => `/crm/contacts/${a.contact_id}`,
  ...over,
});

Object.assign(KINDS, {
  agent_call_request: agentKind({
    icon: Phone,
    title: 'Lead asked for a call',
    action: 'Open lead',
    notes: [880, 1174.7],
    osTitle: '📞 Lead asked for a call',
  }),
  agent_handoff_review: agentKind({
    icon: ClipboardCheck,
    title: 'Screen complete — review',
    action: 'Review',
    notes: [659.3, 987.8],
    osTitle: '✅ Screen complete — review',
    // C-20: reviewing a completed screen means the rail's Screening tab.
    href: (a) => `/crm/contacts/${a.contact_id}?tab=screening`,
  }),
  agent_hostile_or_distress: agentKind({
    band: 'bg-amber-600',
    ring: 'border-amber-300 ring-amber-500/30 dark:border-amber-800',
    icon: AlertTriangle,
    title: 'Needs a human now',
    action: 'Take over',
    notes: [987.8, 659.3],
    osTitle: '⚠️ Conversation needs a human now',
  }),
  agent_human_requested: agentKind({
    icon: UserRound,
    title: 'Lead wants a person',
    action: 'Open lead',
    notes: [880, 987.8],
    osTitle: '🙋 Lead wants a person',
  }),
  // §6.7b: the agent is HOLDING its reply until one of these is taken. The
  // countdown is the point of the card — at T-0 the agent answers itself.
  agent_unknown_answer: agentKind({
    icon: HelpCircle,
    title: 'Question the agent can’t answer',
    action: 'Answer it',
    notes: [659.3, 880],
    osTitle: '❓ Lead asked something the agent can’t answer',
    showsCountdown: true,
    escalatedLine: ({ mmss, expired }) => (expired
      ? 'Agent sent its hold — still worth a real answer'
      : `Take it — agent replies in ${mmss}`),
  }),
  agent_bot_question: agentKind({
    icon: Bot,
    title: 'Lead asked if this is a bot',
    action: 'Take over',
    notes: [740, 987.8],
    osTitle: '🤖 Lead asked if this is a bot',
    showsCountdown: true,
    escalatedLine: ({ mmss, expired }) => (expired
      ? 'Agent disclosed it is automated'
      : `Take it — agent discloses in ${mmss}`),
  }),
});

export const isAgentKind = (k: AlertKind) => k.startsWith('agent_');

export const defFor = (a: CrmAlert): KindDef => KINDS[a.kind] || KINDS.lead_call;

export function fireOsNotification(a: CrmAlert) {
  const def = defFor(a);
  const badge = def.badge(a);
  const detail = def.detail(a);
  fireOsNote({
    title: def.osTitle,
    body: `${a.first_name || 'New lead'}${detail ? ` — ${detail}` : ''}${badge ? ` (${badge.label})` : ''}`,
    tag: `crm-alert:${a.kind}:${a.id}`, // OS collapses dupes across tabs
  });
}

/**
 * The raw OS notification, for the stack's non-alert feeds. Permission is
 * requested once by OverlayHost; everything here is best-effort — a denied or
 * unsupported Notification API must cost nothing but the in-app card, which
 * shows either way.
 */
export function fireOsNote({ title, body, tag }: { title: string; body: string; tag: string }) {
  try {
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      new Notification(title, { body, tag });
    }
  } catch {
    /* denied/unsupported — the in-app card still shows */
  }
}

/** Relative "waiting since" for a card with no deadline. */
export function waitingFor(createdAt: string, now: number): string {
  const mins = Math.max(0, Math.floor((now - new Date(createdAt).getTime()) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  return hrs < 24 ? `${hrs}h ago` : `${Math.floor(hrs / 24)}d ago`;
}
